import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/lib/useApi";
import { formatDate } from "@/lib/api";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ResellerDialog } from "@/components/ResellerDialog";
import { DeleteResellerDialog } from "@/components/DeleteResellerDialog";
import { Plus, Pencil, Trash2, Mail } from "lucide-react";
import { toast } from "sonner";

const statusColor = (r) => {
  if (r.blocked) return "bg-slate-200 text-slate-800 border-slate-400";
  if (r.status === "approved") return "bg-emerald-100 text-emerald-900 border-emerald-300";
  if (r.status === "pending") return "bg-amber-100 text-amber-900 border-amber-300";
  return "bg-slate-100 text-slate-800 border-slate-300";
};
const statusLabel = (r) => (r.blocked ? "Bloccato" : r.status === "approved" ? "Approvato" : r.status === "pending" ? "In attesa" : r.status);

export default function AdminResellers() {
  const api = useApi();
  const qc = useQueryClient();
  const [dialog, setDialog] = useState({ open: false, reseller: null });
  const [toDelete, setToDelete] = useState(null);
  const { data: resellers = [], isLoading } = useQuery({
    queryKey: ["admin-resellers"],
    queryFn: async () => (await api.get("/admin/resellers")).data,
  });
  const inviteUrl = `${window.location.origin}/sign-up`;
  const refresh = () => { qc.invalidateQueries({ queryKey: ["admin-resellers"] }); qc.invalidateQueries({ queryKey: ["admin-customers"] }); };
  const err = (e) => toast.error(e.response?.data?.detail || "Errore");
  const inviteMsg = (inv) => (inv?.sent ? "Credenziali inviate" : inv?.reason === "smtp_not_configured" ? "Credenziali non inviate: configuri prima l'SMTP" : inv?.reason === "invalid_url" ? "Credenziali non inviate: indirizzo del portale non riconosciuto" : `Credenziali non inviate${inv?.reason ? `: ${inv.reason}` : ""}`);
  const save = useMutation({
    mutationFn: async (f) => (dialog.reseller
      ? (await api.put(`/admin/resellers/${dialog.reseller.id}`, { ...f, send_invite: false })).data
      : (await api.post("/admin/resellers", { ...f, invite_url: inviteUrl })).data),
    onSuccess: (r) => {
      const created = !dialog.reseller;
      setDialog({ open: false, reseller: null }); refresh();
      toast.success(created ? `Rivenditore creato (${r.codice_reseller})` : "Rivenditore aggiornato");
      if (created && r.invite) (r.invite.sent ? toast.success : toast.warning)(inviteMsg(r.invite));
    },
    onError: err,
  });
  const invite = useMutation({
    mutationFn: async (id) => (await api.post(`/admin/resellers/${id}/resend-credentials`, { invite_url: inviteUrl })).data,
    onSuccess: (r) => { refresh(); (r.sent ? toast.success : toast.warning)(inviteMsg(r)); },
    onError: err,
  });
  const del = useMutation({
    mutationFn: async (target) => (await api.delete(`/admin/resellers/${toDelete.id}`, { params: target ? { transfer_to: target } : {} })).data,
    onSuccess: (r) => { setToDelete(null); refresh(); toast.success(r.moved_customers ? `Rivenditore cancellato, ${r.moved_customers} clienti trasferiti` : "Rivenditore cancellato"); },
    onError: err,
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Rivenditori</h1>
          <p className="text-sm text-slate-500 mt-1">Anagrafiche, prodotti assegnati, piani provvigionali e blocchi.</p>
        </div>
        <Button onClick={() => setDialog({ open: true, reseller: null })} className="bg-[#B84A2B] hover:bg-[#983C21] text-white" data-testid="btn-new-reseller"><Plus size={14} className="mr-1" /> Nuovo rivenditore</Button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-[#E2DDD5] bg-white">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-slate-500 bg-[#F2F0EB]">
            <tr>
              <th className="text-left px-4 py-3">Rivenditore</th>
              <th className="text-left px-4 py-3">Codice</th>
              <th className="text-left px-4 py-3">P. IVA</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Creato</th>
              <th className="text-left px-4 py-3">Stato</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-500">Caricamento…</td></tr>}
            {!isLoading && resellers.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-500">Nessun rivenditore.</td></tr>}
            {resellers.map((r) => (
              <tr key={r.id} className="border-t border-[#E2DDD5] hover:bg-[#F9F8F6]" data-testid={`row-reseller-${r.id}`}>
                <td className="px-4 py-3 font-medium">{r.company?.ragione_sociale || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs">{r.codice_reseller}</td>
                <td className="px-4 py-3 font-mono text-xs">{r.company?.piva || "—"}</td>
                <td className="px-4 py-3 text-slate-700">{r.email || "—"}{!r.has_login && <div className="text-[11px] text-amber-700" data-testid={`no-login-${r.id}`}>{r.invited_at ? `Credenziali inviate il ${formatDate(r.invited_at)}` : "Non ancora registrato"}</div>}</td>
                <td className="px-4 py-3 text-slate-500">{formatDate(r.created_at)}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusColor(r)}`} data-testid={`status-${r.id}`}>
                    {statusLabel(r)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Button variant="ghost" size="sm" disabled={invite.isPending} onClick={() => window.confirm(`Inviare a ${r.email} nuove credenziali? La password attuale smetterà di funzionare.`) && invite.mutate(r.id)} data-testid={`btn-resend-credentials-${r.id}`}><Mail size={14} className="mr-1" /> Reinvia credenziali</Button>
                  <Button variant="ghost" size="sm" onClick={() => setDialog({ open: true, reseller: r })} data-testid={`btn-edit-reseller-${r.id}`}><Pencil size={14} /></Button>
                  <Button variant="ghost" size="sm" className="text-rose-700 hover:text-rose-800" onClick={() => setToDelete(r)} data-testid={`btn-delete-reseller-${r.id}`}><Trash2 size={14} /></Button>
                  <Link to={`/admin/resellers/${r.id}`} className="ml-2 text-[#B84A2B] hover:underline text-sm font-medium" data-testid={`link-detail-${r.id}`}>Dettaglio →</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ResellerDialog open={dialog.open} onOpenChange={(v) => setDialog({ open: v, reseller: v ? dialog.reseller : null })} reseller={dialog.reseller} onSave={(f) => save.mutate(f)} saving={save.isPending} />
      <DeleteResellerDialog reseller={toDelete} resellers={resellers} onOpenChange={(v) => !v && setToDelete(null)} onConfirm={(t) => del.mutate(t)} deleting={del.isPending} />
    </div>
  );
}
